<script setup lang="ts">
import {ref} from "vue";
import {Button} from "@/core/components/ui/button";
import MSSanteDialog from "@/mssante/components/MSSanteDialog.vue";
import {MSSanteAttachment, MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import {useToastStore} from "@/stores/toast.store";
import {Loader2} from "@lucide/vue";

const {prefills, attachmentsLoader, attachmentsRequired = false, consumerKey, disabled = false, compact = false} = defineProps<{
    /** The messages to propose, in proposal order: the first is the priority one. */
    prefills?: MSSantePrefill[];
    /** Lazily loads extra attachments (e.g. a generated PDF) when the dialog opens. */
    attachmentsLoader?: () => Promise<MSSanteAttachment[]>;
    /** The loaded attachments are the purpose of the message: without them, the dialog stays closed. */
    attachmentsRequired?: boolean;
    /** Enables the consumer favorite recipients suggestions. */
    consumerKey?: string;
    /** The patient display name, shown above the suggested recipients. */
    patientName?: string;
    /** The patient customer id, required to write to their Mon espace santé. */
    customerId?: number;
    disabled?: boolean;
    /** Renders the logo alone, to sit in a row of icon buttons. */
    compact?: boolean;
}>();
const emit = defineEmits<{
    (e: 'sent'): void;
}>();

// Multi-root template: forward the attributes (e.g. a class harmonizing the
// button with its neighbors) to the button explicitly.
defineOptions({inheritAttrs: false});

const toast = useToastStore();

const open = ref<boolean>(false);
const preparing = ref<boolean>(false);
const resolvedPrefills = ref<MSSantePrefill[]>([]);

/**
 * Load the lazy attachments, which belong to the priority message, then open
 * the dialog, unless required attachments failed to load.
 */
const openDialog = async () => {
    preparing.value = true;

    const loaded: MSSanteAttachment[] = [];

    try {
        if (attachmentsLoader) {
            loaded.push(...await attachmentsLoader());
        }
    } catch {
        toast.showError({content: 'Les pièces jointes n\'ont pas pu être préparées.'});

        if (attachmentsRequired) {
            return;
        }
    } finally {
        preparing.value = false;
    }

    resolvedPrefills.value = (prefills ?? []).map((prefill, position) => position === 0
        ? {...prefill, attachments: [...(prefill.attachments ?? []), ...loaded]}
        : prefill);

    open.value = true;
};
</script>

<template>
  <Button
    v-bind="$attrs"
    :disabled="disabled || preparing"
    variant="outline"
    :class="['text-primary', compact && 'rounded-full']"
    @click="openDialog"
  >
    <Loader2 v-if="preparing" class="size-4 animate-spin" />
    <img v-else src="@/assets/images/logos/logo-ms-sante.png" class="h-6 rounded-full" alt="MSSante">
    <slot v-if="!compact">
      Informer
    </slot>
  </Button>
  <MSSanteDialog
    v-model:open="open"
    :prefills="resolvedPrefills"
    :consumer-key="consumerKey"
    :patient-name="patientName"
    :customer-id="customerId"
    @sent="emit('sent')"
  />
</template>
